import * as B from '@blueprintjs/core';
import React from 'react';
import ReactDOM from 'react-dom';

import {useShareLink} from 'app/providers/ShareLinkProvider';
import {USER_ROLE_REGULAR} from 'app/utils/constants';
import {useUserInfo} from 'app/utils/featureFlags';
import * as mapUtils from 'app/utils/mapUtils';
import {atLeastUserRole} from 'app/utils/userUtils';

import MapContent from './MapContent';
import cs from './StyleControl.module.scss';

import {MapControlPosition} from '.';

interface Props {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  position: MapControlPosition;
  imageRefs: mapUtils.MapImageRefs;
}

const ShareControl: React.FC<Props> = ({map, isMapLoaded, position, imageRefs}) => {
  const shareControlButtonEl = document.createElement('div');
  const shareControlButton = new mapUtils.PortalControl(shareControlButtonEl);
  const [_, profile] = useUserInfo();
  const hasSharePermissions = atLeastUserRole(USER_ROLE_REGULAR, profile!);

  const {openShareLinkModal, isShareLinkModalOpen, hasUnshareableConfiguration} = useShareLink();

  const disableSharing = React.useMemo(
    () => hasUnshareableConfiguration(imageRefs),
    [hasUnshareableConfiguration, imageRefs]
  );

  return (
    <React.Fragment>
      <MapContent
        map={map}
        isMapLoaded={isMapLoaded}
        controls={[shareControlButton]}
        controlPosition={position}
      />
      {ReactDOM.createPortal(
        // Using a popover with interactionKind=hover instead of a tooltip to allow for click inside
        <B.Popover
          interactionKind="hover"
          content={
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem'}}>
              <span>
                {!hasSharePermissions
                  ? 'Contact your organization administrator to enable sharing.'
                  : disableSharing
                    ? "This data isn't available to share publicly"
                    : 'Share property map'}
              </span>
              <B.AnchorButton
                icon={'info-sign'}
                href="https://lens.tech/support/article/151-lens-share-link"
                target="_blank"
                small
                minimal
                className={cs.mapIcon}
              />
            </div>
          }
          position={B.Position.RIGHT}
        >
          <B.AnchorButton
            id="share-control"
            icon="share"
            active={isShareLinkModalOpen}
            className={cs.mapIcon}
            disabled={!hasSharePermissions || disableSharing}
            onClick={() => openShareLinkModal(imageRefs)}
          />
        </B.Popover>,
        shareControlButtonEl
      )}
    </React.Fragment>
  );
};

export default ShareControl;
